/**
 * Time-based staff notifications (Traci, 25 Sep 2026). Pure: rows in, staff_notifications rows out; the server job
 * (src/server/jobs/staffTimers.js) writes them every 15 minutes and the dedupe key makes each go out once.
 *
 *   Flags     an assigned Flag still open 24 hours after it was raised is Overdue: its owner is told (the assigned
 *             person, else the department)
 *   Mentions  someone @mentioned on a Flag who has not replied within 24 hours is reminded (unless it was resolved)
 *   Editing   Initial review due 48 hours after the upload; Issues resolved or approved within 3 days; the gallery
 *             edited within 7 days of approval. When one passes, Editing is told.
 */
export const FLAG_OVERDUE_HOURS = 24
// Editing is told as a deadline passes, not about history: only deadlines that lapsed in the last 48 hours notify (the
// first run on 25 Sep would otherwise have sent 152 notices for old bookings; the board still shows those as Overdue).
export const EDITING_LOOKBACK_HOURS = 48
export const MENTION_REPLY_HOURS = 24
export const EDITING_TIMERS = {
  pending_review: { hours: 48, title: 'Initial review overdue', what: 'uploaded media has waited more than 48 hours for initial review' },
  has_issues: { hours: 72, title: 'Media issue unresolved', what: 'media has sat in Issues more than 3 days without being resolved or approved' },
  approved: { hours: 168, title: 'Gallery editing overdue', what: 'the gallery has not been finished within 7 days of approval' }
}

const HOUR = 3600000
const OPEN = new Set(['open', 'escalated'])
const PLACEHOLDER_TALENT = new Set(['410935f9-6034-40b3-a65f-e9863de60e58', '7c7191dc-c5e5-43ed-b772-083463a00aa4'])
const ms = v => (v ? Date.parse(v) : NaN)
const day = v => String(v || '').slice(0, 10)
const over = (start, hours, now) => Number.isFinite(ms(start)) && now - ms(start) > hours * HOUR

/** Is this Flag overdue (open 24+ hours)? Used by the notice and by the Notification Center's OVERDUE badge. */
export function flagIsOverdue(flag, now = Date.now()) {
  return Boolean(flag) && OPEN.has(String(flag.status || 'open').toLowerCase()) && over(flag.created_at, FLAG_OVERDUE_HOURS, now)
}

/** @param flags flagged_accounts rows · names id → name (clients) */
export function flagOverdueNotices({ flags = [], names = new Map(), now = Date.now() }) {
  const out = []
  for (const f of flags) {
    if (!flagIsOverdue(f, now) || (!f.assigned_to && !f.assigned_to_role)) continue
    const client = names.get(f.user_id) || null
    out.push({
      type: 'flag_overdue',
      title: 'Assigned Flag is overdue',
      message: `"${f.reason || 'Flag'}"${client ? ` for ${client}` : ''} has been open more than 24 hours and is still unresolved.`,
      flag_id: f.id,
      account_name: client,
      target_user_id: f.assigned_to || null,
      target_roles: f.assigned_to ? [] : [f.assigned_to_role],
      severity: 'high',
      dedupe_key: `flag_overdue:${f.id}`
    })
  }
  return out
}

/**
 * @param comments flag_comments rows (id, flag_id, author_id, author_name, mentions, created_at), newest or oldest first
 * @param flags    flagged_accounts rows for those comments (id, status, reason, user_id)
 */
export function mentionReminderNotices({ comments = [], flags = [], names = new Map(), now = Date.now() }) {
  const flagBy = new Map(flags.map(f => [f.id, f]))
  const out = []
  for (const c of comments) {
    const mentioned = Array.isArray(c.mentions) ? c.mentions : []
    if (!mentioned.length || !over(c.created_at, MENTION_REPLY_HOURS, now)) continue
    const f = flagBy.get(c.flag_id)
    if (!f || !OPEN.has(String(f.status || 'open').toLowerCase())) continue
    for (const user of mentioned) {
      if (user === c.author_id) continue
      const replied = comments.some(r => r.flag_id === c.flag_id && r.author_id === user && ms(r.created_at) > ms(c.created_at))
      if (replied) continue
      const client = names.get(f.user_id) || null
      out.push({
        type: 'flag_mention_reminder',
        title: 'Still waiting on your reply',
        message: `${c.author_name || 'Someone'} mentioned you on "${f.reason || 'a Flag'}"${client ? ` for ${client}` : ''} more than 24 hours ago.`,
        flag_id: f.id,
        account_name: client,
        target_user_id: user,
        target_roles: [],
        severity: 'medium',
        dedupe_key: `mention_reminder:${c.id}:${user}`
      })
    }
  }
  return out
}

/** Media types a booking carries, with the roles that deliver each (placeholders excluded). */
function mediaRoles(b) {
  const out = []
  const photo = [['photographer', b.photographer_id], ['second_photographer', b.second_photographer_id]].filter(([, id]) => id && !PLACEHOLDER_TALENT.has(id)).map(([r]) => r)
  if (photo.length) out.push({ media: 'photo', roles: photo })
  const video = [['videographer', b.videographer_id], ['second_videographer', b.second_videographer_id]].filter(([, id]) => id && !PLACEHOLDER_TALENT.has(id)).map(([r]) => r)
  if (video.length) out.push({ media: 'video', roles: video })
  return out
}

/**
 * @param bookings rows with review columns (photo_/video_review_status, photo_/video_reviewed_at, photo_/video_uploaded_at,
 *                 review_status, reviewed_at, media_uploaded_at, *_id slots, booking_status, is_test, customer_id)
 * @param forms    job_completion_forms rows: booking_id, talent_role, submitted_at
 */
export function editingTimerNotices({ bookings = [], forms = [], names = new Map(), now = Date.now(), lookbackHours = EDITING_LOOKBACK_HOURS }) {
  const out = []
  for (const b of bookings) {
    if (b.is_test || String(b.booking_status || '').toLowerCase().startsWith('cancel')) continue
    for (const { media, roles } of mediaRoles(b)) {
      const status = String(b[`${media}_review_status`] || (media === 'photo' ? b.review_status : '') || '').toLowerCase()
      const rule = EDITING_TIMERS[status]
      if (!rule) continue
      let start
      if (status === 'pending_review') {
        const submitted = forms.filter(f => f.booking_id === b.id && roles.includes(f.talent_role || 'photographer')).map(f => f.submitted_at).filter(Boolean).sort()
        start = submitted[0] || b[`${media}_uploaded_at`] || b.media_uploaded_at
      } else {
        start = b[`${media}_reviewed_at`] || b.reviewed_at
      }
      if (!over(start, rule.hours, now)) continue
      if (now - (ms(start) + rule.hours * HOUR) > lookbackHours * HOUR) continue
      const client = names.get(b.customer_id) || null
      out.push({
        type: 'editing_overdue',
        title: rule.title,
        message: `${client || 'A booking'} (${media === 'video' ? 'video' : 'photos'}, event ${day(b.event_date)}): ${rule.what}.`,
        booking_id: b.id,
        account_name: client,
        target_user_id: null,
        target_roles: ['editing'],
        severity: 'high',
        dedupe_key: `editing_overdue:${status}:${b.id}:${media}:${day(start)}`
      })
    }
  }
  return out
}
